import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Repeat, ArrowLeft } from "lucide-react";
import { ROLE_PT, labelPt } from "@/lib/labels";

const ROLE_COLORS = {
  owner: "bg-emerald-100 text-emerald-800",
  manager: "bg-blue-100 text-blue-800",
  staff: "bg-neutral-100 text-neutral-700",
  accountant: "bg-purple-100 text-purple-800",
};

export default function QuickSwitcher() {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [picked, setPicked] = useState(null);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem("oplyx_user") || "{}");

  useEffect(() => {
    if (!open) return;
    setPicked(null); setPassword("");
    api.get("/auth/switchable-users").then(r => setMembers(r.data || [])).catch(()=>{});
  }, [open]);

  const doSwitch = async (e) => {
    e?.preventDefault();
    if (!picked || !password) return;
    setLoading(true);
    try {
      const r = await api.post("/auth/switch", { email: picked.email, password });
      localStorage.setItem("oplyx_token", r.data.token);
      localStorage.setItem("oplyx_user", JSON.stringify(r.data.user));
      // sync branch to the new user's assigned active_branch_id
      if (r.data.user?.active_branch_id) localStorage.setItem("oplyx_branch", r.data.user.active_branch_id);
      else localStorage.removeItem("oplyx_branch");
      toast.success(`Conectado como ${r.data.user.name}`);
      setOpen(false);
      // reload to refresh all role-dependent UI (sidebar, widgets, restrictions)
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Senha incorreta");
    } finally {
      setLoading(false);
    }
  };

  const selectable = members.filter(m => m.email !== user.email);

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-full justify-start text-neutral-600"
        onClick={() => setOpen(true)}
        data-testid="quick-switcher-btn"
      >
        <Repeat className="w-4 h-4 mr-2" /> Trocar usuário
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="quick-switcher-dialog">
          <DialogHeader>
            <DialogTitle>Trocar usuário rapidamente</DialogTitle>
          </DialogHeader>
          {!picked ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Selecione um membro da equipe para continuar operando sem sair do sistema.</p>
              {selectable.length === 0 && (
                <div className="text-sm text-center text-muted-foreground py-6">Nenhum outro usuário cadastrado. Adicione colaboradores em <b>Equipe</b>.</div>
              )}
              <div className="max-h-72 overflow-auto space-y-1">
                {selectable.map(m => (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => setPicked(m)}
                    className="w-full flex items-center justify-between gap-2 p-3 rounded-lg border hover:bg-neutral-50 text-left transition"
                    data-testid={`switch-user-${m.email}`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{m.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                    </div>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${ROLE_COLORS[m.role] || "bg-neutral-100"}`}>{labelPt(ROLE_PT, m.role)}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <form onSubmit={doSwitch} className="space-y-3">
              <div className="p-3 rounded-lg bg-neutral-50 border flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{picked.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{picked.email}</div>
                </div>
                <button type="button" onClick={()=>setPicked(null)} className="text-xs text-muted-foreground hover:underline flex items-center gap-1">
                  <ArrowLeft className="w-3 h-3"/>trocar
                </button>
              </div>
              <div>
                <Label htmlFor="switch-password">Senha de {picked.name.split(" ")[0]}</Label>
                <Input
                  id="switch-password"
                  type="password"
                  value={password}
                  onChange={e=>setPassword(e.target.value)}
                  autoFocus
                  required
                  data-testid="switch-password-input"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="switch-confirm-btn">
                {loading ? "Trocando…" : "Entrar como este usuário"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
